// src/components/patient/billing/InvoiceView.jsx
//
// Read-only presentation of the canonical appointment invoice, shared by the
// Invoices page and the appointment detail Billing section.
//
//   * every state (invoice/payment status, refund review) and every amount is
//     shown exactly as the server sent it -- nothing is computed or summed here;
//   * money is always shown with the invoice's own currency;
//   * the PDF is downloaded as a blob through the authenticated API; a failure
//     is shown as readable text.

import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { readApiError } from "../../../lib/apiErrors";
import { saveBlob } from "../../../lib/download";
import { formatDateTimeInZone } from "../../../lib/displayTimezone";
import { Pill, classNames, modeLabel, money } from "../booking/bookingUi";

export const PAYMENT_TONES = {
  not_paid: "warn",
  paid: "success",
  partially_refunded: "violet",
  refunded: "neutral",
};

export const INVOICE_STATUS_TONES = {
  issued: "info",
  cancelled: "danger",
};

export function raiseInvoiceIssueHref(invoice) {
  return `/patient/tickets/new?category=invoice&invoice_id=${encodeURIComponent(invoice?.id || "")}`;
}

export function InvoiceIcon({ className = "h-6 w-6" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 3h10a2 2 0 0 1 2 2v16l-3-2-3 2-3-2-3 2-2-1.3V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function InvoiceStateBadges({ invoice }) {
  if (!invoice) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Pill tone={INVOICE_STATUS_TONES[invoice.invoice_status] || "neutral"}>{invoice.invoice_status_label || invoice.invoice_status}</Pill>
      <Pill tone={PAYMENT_TONES[invoice.payment_status] || "neutral"}>{invoice.payment_status_label || invoice.payment_status}</Pill>
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-text">{children || "—"}</dd>
    </div>
  );
}

function TotalRow({ label, value, strong = false }) {
  return (
    <div className={classNames("flex items-center justify-between gap-4 px-4 py-2 text-sm", strong && "bg-slate-50 font-semibold")}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export function InvoiceNotices({ invoice }) {
  if (!invoice) return null;
  return (
    <>
      {invoice.invoice_status === "cancelled" ? (
        <p className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800" data-testid="invoice-cancelled">
          This invoice is cancelled. It is kept here for your records.
        </p>
      ) : null}
      {invoice.refund_review_required ? (
        <p className="rounded-2xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="invoice-refund-review">
          <span className="font-semibold">Refund under review.</span> This appointment was cancelled after payment. The clinic is reviewing your refund; this page will update when the refund is processed.
        </p>
      ) : null}
    </>
  );
}

/** Full read-only breakdown: parties, appointment link, lines, totals and states. */
export default function InvoiceView({ invoice, timeZone, showAppointment = true }) {
  if (!invoice) return null;
  const currency = invoice.currency;
  const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const apptZone = timeZone || invoice.appointment_timezone;

  return (
    <div className="space-y-4" data-testid="invoice-view">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Invoice</div>
          <div className="text-base font-semibold text-text">{invoice.invoice_number}</div>
        </div>
        <InvoiceStateBadges invoice={invoice} />
      </div>

      <InvoiceNotices invoice={invoice} />

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Issued on">{formatDateTimeInZone(invoice.issued_at, timeZone)}</Field>
        <Field label="Paid on">{invoice.paid_at ? formatDateTimeInZone(invoice.paid_at, timeZone) : "—"}</Field>
        <Field label="Currency">{currency}</Field>
        <Field label="Patient">{invoice.patient_name}</Field>
        <Field label="Doctor">{invoice.doctor_name}</Field>
        <Field label="Clinic">{invoice.clinic_name}</Field>
        <Field label="Service">{invoice.service_name}</Field>
        <Field label="Billed by">{invoice.entity_type === "clinic" ? "Clinic" : invoice.entity_type}</Field>
        {showAppointment ? (
          <Field label="Appointment">
            {invoice.appointment_id ? (
              <Link to={`/patient/appointments/${encodeURIComponent(invoice.appointment_id)}`} className="font-semibold text-primary hover:underline">
                {invoice.appointment_number || "View appointment"}
              </Link>
            ) : (
              invoice.appointment_number
            )}
          </Field>
        ) : null}
        <Field label="Appointment time">
          {invoice.appointment_start_at ? formatDateTimeInZone(invoice.appointment_start_at, apptZone) : "—"}
        </Field>
        <Field label="Mode">{modeLabel(invoice.consultation_mode) || invoice.consultation_mode}</Field>
        <Field label="Location">{invoice.location_name}</Field>
      </dl>

      <div className="overflow-hidden rounded-2xl border border-border">
        <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Line items</div>
        {lines.length ? (
          <ul className="divide-y divide-border">
            {lines.map((line, index) => (
              <li key={`${line.item_name}-${index}`} className="grid grid-cols-2 gap-x-4 gap-y-1 px-4 py-3 text-sm sm:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))]" data-testid="invoice-line">
                <div className="col-span-2 min-w-0 sm:col-span-1">
                  <div className="font-semibold text-text">{line.item_name}</div>
                  {line.description ? <div className="text-xs text-muted">{line.description}</div> : null}
                </div>
                <div className="text-muted sm:text-right"><span className="sm:sr-only">Qty </span>{line.quantity} × {money(line.unit_price, currency)}</div>
                <div className="text-right text-muted"><span className="sm:sr-only">Discount </span>{money(line.discount_amount, currency)}</div>
                <div className="text-muted sm:text-right"><span className="sm:sr-only">Tax </span>{money(line.tax_amount, currency)}</div>
                <div className="text-right font-semibold text-text"><span className="sr-only">Line total </span>{money(line.total_amount, currency)}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-3 text-sm text-muted">No line items on this invoice.</p>
        )}
      </div>

      <dl className="ml-auto w-full max-w-sm divide-y divide-border overflow-hidden rounded-2xl border border-border" data-testid="invoice-totals">
        <TotalRow label="Subtotal" value={money(invoice.subtotal_amount, currency)} />
        <TotalRow label="Discount" value={money(invoice.discount_amount, currency)} />
        <TotalRow label="Tax" value={money(invoice.tax_amount, currency)} />
        <TotalRow label="Total" value={money(invoice.total_amount, currency)} strong />
        <TotalRow label="Amount paid" value={money(invoice.amount_paid, currency)} />
        <TotalRow label="Amount due" value={money(invoice.amount_due, currency)} strong />
      </dl>
    </div>
  );
}

/**
 * PDF download with a duplicate-click guard. `download(invoiceId, invoiceNumber)`
 * resolves to { blob, filename } (or a bare Blob).
 */
export function InvoicePdfButton({ invoice, download, label = "Download invoice (PDF)", className = "" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);

  async function onClick() {
    if (inFlight.current || !invoice) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await download(invoice.id, invoice.invoice_number);
      const isBlob = typeof Blob !== "undefined" && result instanceof Blob;
      const blob = isBlob ? result : result?.blob;
      if (!blob) throw new Error("empty download");
      saveBlob(blob, (!isBlob && result?.filename) || `${invoice.invoice_number || "invoice"}.pdf`);
    } catch (err) {
      setError(readApiError(err, "The invoice PDF could not be downloaded. Please try again."));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="min-w-0">
      <button type="button" onClick={onClick} disabled={busy} aria-busy={busy || undefined}
        className={classNames("rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text hover:bg-slate-50 disabled:opacity-60", className)}>
        {busy ? "Preparing PDF…" : label}
      </button>
      {error ? <p role="alert" className="mt-2 text-sm text-rose-700" data-testid="invoice-pdf-error">{error}</p> : null}
    </div>
  );
}
